<template>
  <div class="tree">
    <TreeItem
      v-for="item in items"
      ref="children"
      :key="item.id"
      :level="1"
      :item="item"
      :editable="editable"
      :children-key="childrenKey"
      :name-key="nameKey"
      :add-btn-text="addBtnText"
      :tree-opened-values="treeOpenedValues"
      :tree-values="treeValues"
      @itemSelected="itemSelected"
      @nameEdited="nameEdited"
      @parentChange="parentChanged"
      @addItem="addItem"
      @deleteItem="deleteItem"
      @open="openItem"
    />
  </div>
</template>

<script>
/**
 * Tree component that displays tree-item(s)
 *
 * @vue-prop {Object|Array} value - If props.multiple is true, this should be an array of ids.
 *                                  Otherwise, selected item
 * @vue-prop {Array} items - All selectable items in a tree-like format :
 *                           { id: 1, nameKey: 'Name', childrenKey: [{...}] }
 * @vue-prop {String} [childrenKey='zones'] - Key to use for the children of each tree leaf (see above)
 * @vue-prop {String} [nameKey='name'] - Key to use as the name (title) of each leaf (see above)
 * @vue-prop {String} [addBtnText='Add a location'] - If the tree is editable, title of the button to add leaves
 * @vue-prop {Boolean} editable - Allow adding leaves and editing the text of each one
 * @vue-prop {Boolean} multiple - True to allow selecting multiple items
 *
 */

import TreeItem from '@/components/tree-item.vue';

export default {

  name: 'Tree',

  components: {
    TreeItem,
  },

  props: {
    value: {
      type: [Object, Array],
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    childrenKey: {
      type: String,
      default: 'zones',
    },
    nameKey: {
      type: String,
      default: 'name',
    },
    addBtnText: {
      type: String,
      default() { return this.$t('message.ADD_A_LOCATION'); },
    },
    editable: Boolean,
    multiple: Boolean,
  },

  data() {
    return {
      treeOpenedValues: {},
    };
  },

  computed: {
    /** Array: Currently selected ids in the tree */
    treeValues() {
      if (this.value) {
        return this.multiple ? this.value : [this.value.id];
      }
      return [];
    },
  },

  methods: {
    /** Select an item */
    itemSelected(item, selected) {
      if (!this.multiple) {
        this.$emit('input', item);
      } else {
        const idx = this.value.findIndex(it => Number(it) === Number(item.id));
        if (selected && idx === -1) {
          this.$emit('input', [...this.value, item.id]);
        } else if (!selected && idx !== -1) {
          const newValue = this.value.slice();
          newValue.splice(idx, 1);
          this.$emit('input', newValue);
        }
      }
    },
    /** Open a tree item to display its children */
    openItem(item, isOpened) {
      this.treeOpenedValues = { ...this.treeOpenedValues, [`${item.id}`]: !!isOpened };
    },
    parentChanged(params) {
      this.$emit('parentChange', params);
    },
    nameEdited(subitem) {
      this.$emit('nameEdited', subitem);
    },
    addItem(subitem) {
      this.$emit('addItem', subitem);
    },
    deleteItem(subitem) {
      this.$emit('deleteItem', subitem);
    },
  },
};
</script>
